import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import dagre from '@dagrejs/dagre'
import { Position, useNodesInitialized, useReactFlow } from '@xyflow/react'

import { EdgeUI, NodeUI } from 'modules/workflows/components/Node/types'

/**
 * After dagre computes the layout, condition branches may appear in an arbitrary
 * horizontal order because dagre uses a barycenter heuristic that ignores insertion order.
 * This function reorders them so conditionIf branches appear left-to-right by priority,
 * and the conditionElse branch is always last (rightmost).
 */
const reorderConditionBranches = (
  graph: dagre.graphlib.Graph,
  nodesToLayout: NodeUI[],
  edgesToLayout: EdgeUI[]
): void => {
  const nodeMap = new Map(nodesToLayout.map((n) => [n.id, n]))

  // Group condition branches by their parent condition node
  const branchGroups = new Map<string, NodeUI[]>()
  edgesToLayout.forEach((e) => {
    const source = nodeMap.get(e.source)
    const target = nodeMap.get(e.target)
    if (
      source?.type === 'condition' &&
      target &&
      (target.type === 'conditionIf' || target.type === 'conditionElse')
    ) {
      const group = branchGroups.get(e.source) || []
      group.push(target)
      branchGroups.set(e.source, group)
    }
  })

  // Build outgoing adjacency map (only needed if we have branches to reorder)
  if (branchGroups.size === 0) return
  const outgoing = new Map<string, string[]>()
  edgesToLayout.forEach((e) => {
    const targets = outgoing.get(e.source) || []
    targets.push(e.target)
    outgoing.set(e.source, targets)
  })

  branchGroups.forEach((branches) => {
    if (branches.length <= 1) return

    // Collect current x positions sorted left-to-right
    const sortedXPositions = branches.map((b) => graph.node(b.id).x).sort((a, b) => a - b)

    // Desired order: conditionIf sorted by priority, conditionElse last
    const sortedBranches = [...branches].sort((a, b) => {
      if (a.type === 'conditionElse') return 1
      if (b.type === 'conditionElse') return -1
      const pA = a.type === 'conditionIf' ? a.data.comparison.priority : 0
      const pB = b.type === 'conditionIf' ? b.data.comparison.priority : 0
      return pA - pB
    })

    // Swap branch x positions and shift all descendants by the same delta
    sortedBranches.forEach((branch, index) => {
      const dagreNode = graph.node(branch.id)
      const targetX = sortedXPositions[index]
      if (!dagreNode || targetX === undefined) return

      const dx = targetX - dagreNode.x
      if (Math.abs(dx) < 0.5) return

      dagreNode.x = targetX

      // Shift all descendants (BFS) so the entire subtree stays aligned
      const queue = outgoing.get(branch.id)?.slice() || []
      let descendantId = queue.shift()
      while (descendantId) {
        const descendant = graph.node(descendantId)
        if (descendant) descendant.x += dx
        const next = outgoing.get(descendantId) || []
        queue.push(...next)
        descendantId = queue.shift()
      }
    })
  })
}

export type Options = {
  rankdir?: 'TB' | 'BT' | 'LR' | 'RL'
  nodeWidth: number
  defaultNodeHeight?: number
  nodesep?: number
  ranksep?: number
}

export const useDagreAutoLayout = (
  nodes: NodeUI[],
  edges: EdgeUI[],
  setNodes: Dispatch<SetStateAction<NodeUI[]>>,
  options: Options
) => {
  const rankdir = options?.rankdir ?? 'TB'
  const nodeWidth = options.nodeWidth
  const defaultNodeHeight = options?.defaultNodeHeight ?? 60
  const nodesep = options?.nodesep ?? 40
  const ranksep = options?.ranksep ?? 200

  const [nodeHeights, setNodeHeights] = useState<Record<string, number>>({})
  const [isLaidOut, setIsLaidOut] = useState<boolean>(false)
  const nodesInitialized = useNodesInitialized()
  const { fitView } = useReactFlow()

  const layoutWithDagre = useCallback(
    (
      nodesToLayout: NodeUI[],
      edgesToLayout: EdgeUI[],
      heights: Record<string, number>
    ): NodeUI[] => {
      const graph = new dagre.graphlib.Graph()
      graph.setGraph({ rankdir, nodesep, ranksep, ranker: 'network-simplex' })
      graph.setDefaultEdgeLabel(() => ({}))

      nodesToLayout.forEach((n) => {
        const height = heights[n.id] ?? defaultNodeHeight
        graph.setNode(n.id, { width: nodeWidth, height })
      })
      edgesToLayout.forEach((e) => {
        graph.setEdge(e.source, e.target, { minlen: 1 })
      })

      dagre.layout(graph)

      // Reorder condition branches by priority (else last)
      reorderConditionBranches(graph, nodesToLayout, edgesToLayout)

      // Compute top alignment per group of siblings: group by center Y and use max height
      const maxHeightPerSiblings = nodesToLayout.reduce<Record<number, number>>((acc, n) => {
        const d = graph.node(n.id)
        if (!d) return acc
        const siblingsKey = Math.round(d.y)
        acc[siblingsKey] = Math.max(acc[siblingsKey] ?? 0, d.height)
        return acc
      }, {})

      return nodesToLayout.map((n) => {
        const dagreNode = graph.node(n.id)
        if (!dagreNode) return n
        const siblingsKey = Math.round(dagreNode.y)
        const groupMaxH = maxHeightPerSiblings[siblingsKey] ?? dagreNode.height
        return {
          ...n,
          position: {
            x: dagreNode.x - dagreNode.width / 2,
            y: dagreNode.y - groupMaxH / 2,
          },
          sourcePosition: rankdir === 'TB' || rankdir === 'BT' ? Position.Bottom : Position.Right,
          targetPosition: rankdir === 'TB' || rankdir === 'BT' ? Position.Top : Position.Left,
        }
      })
    },
    [rankdir, nodeWidth, defaultNodeHeight, nodesep, ranksep]
  )

  const relayout = useCallback(
    (fit = false) => {
      const repositionedNodes = layoutWithDagre(nodes, edges, nodeHeights)
      setNodes(repositionedNodes)
      if (fit) requestAnimationFrame(() => fitView({ padding: 0.2 }))
    },
    [edges, fitView, layoutWithDagre, nodeHeights, nodes, setNodes]
  )

  const onSizeNode = useCallback((id: string, _width: number, height: number) => {
    setNodeHeights((prev) => {
      const current = prev[id]
      if (current && Math.abs(current - height) < 0.5) return prev
      return { ...prev, [id]: height }
    })
  }, [])

  // When nodes are initialized and heights are known, lay out once and fit view
  useEffect(() => {
    if (isLaidOut) return
    if (!nodesInitialized) return
    if (nodes.length === 0) return
    const allMeasured = nodes.every((n) => nodeHeights[n.id] != null)
    if (!allMeasured) return
    relayout(true)
    setIsLaidOut(true)
  }, [nodesInitialized, nodes, nodeHeights, isLaidOut, relayout])

  return useMemo(() => ({ onSizeNode, isLaidOut, relayout }), [onSizeNode, isLaidOut, relayout])
}
